import React, { useEffect, useRef, useState } from 'react';
import { Mail, Phone, MapPin, Linkedin, ArrowUpRight, Globe, Send } from 'lucide-react';

const Contact = ({ data }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const contactCards = [
    {
      icon: Mail,
      title: "Email",
      value: data.email,
      href: `mailto:${data.email}`,
      action: "Send Email"
    },
    {
      icon: Phone,
      title: "Phone",
      value: data.phone,
      href: `tel:${data.phone}`,
      action: "Call Now"
    },
    {
      icon: Linkedin,
      title: "LinkedIn",
      value: "View Profile",
      href: data.linkedin,
      action: "Connect",
      external: true
    }
  ];

  return (
    <section id="contact" ref={sectionRef} className="py-24 md:py-32 bg-[#0a1628] relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#d4af37]/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <div className={`mb-16 text-center ${isVisible ? 'animate-fade-up' : 'opacity-0'}`}>
          <span className="text-[#d4af37] text-sm font-semibold uppercase tracking-[0.2em]">Get In Touch</span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4">
            Let's <span className="text-[#d4af37]">Connect</span>
          </h2>
          <div className="w-20 h-1 bg-[#d4af37] mt-6 mx-auto" />
          <p className="text-[#94a3b8] text-lg mt-6 max-w-2xl mx-auto">
            Open to discussing opportunities in wealth management, fintech innovation, 
            and digital transformation leadership.
          </p>
        </div>

        {/* Contact Cards */}
        <div className={`grid md:grid-cols-3 gap-6 max-w-4xl mx-auto ${isVisible ? 'animate-fade-up delay-200' : 'opacity-0'}`}>
          {contactCards.map((card, index) => (
            <a
              key={index}
              href={card.href}
              target={card.external ? "_blank" : undefined}
              rel={card.external ? "noopener noreferrer" : undefined}
              className="group bg-[#1a2744] p-8 border border-[#d4af37]/10 hover:border-[#d4af37]/50 transition-all duration-500 hover:-translate-y-2 text-center"
            >
              <div className="w-16 h-16 bg-[#d4af37]/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-[#d4af37] transition-colors duration-300">
                <card.icon size={28} className="text-[#d4af37] group-hover:text-[#0a1628] transition-colors" />
              </div>
              <h3 className="text-white font-bold text-lg mb-2">{card.title}</h3>
              <p className="text-[#94a3b8] text-sm mb-4 group-hover:text-white transition-colors break-all">
                {card.value}
              </p>
              <span className="inline-flex items-center gap-1 text-[#d4af37] text-sm font-medium group-hover:gap-2 transition-all">
                {card.action}
                <ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </span>
            </a>
          ))}
        </div>

        {/* Location & Languages */}
        <div className={`mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 ${isVisible ? 'animate-fade-up delay-400' : 'opacity-0'}`}>
          <div className="inline-flex items-center gap-3 bg-[#1a2744] px-6 py-3 border border-[#d4af37]/20">
            <MapPin size={18} className="text-[#d4af37]" />
            <span className="text-[#94a3b8]">{data.location}</span>
          </div>
          {data.languages && (
            <div className="inline-flex items-center gap-3 bg-[#1a2744] px-6 py-3 border border-[#d4af37]/20">
              <Globe size={18} className="text-[#d4af37]" />
              <span className="text-[#94a3b8]">{data.languages.join(" • ")}</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const Footer = ({ data }) => {
  const currentYear = new Date().getFullYear();

  const activities = [
    "CII - YI Net Bangalore Chapter",
    "Finance Club 'Arthakul' at Welingkar",
    "Alumni Secretary at Welingkar",
    "Trek Leader - Sahyadri Mountains"
  ];

  return (
    <footer className="bg-[#0f1d32] border-t border-[#d4af37]/10">
      {/* Activities Section */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <div className="text-center mb-8">
          <h3 className="text-white font-semibold text-lg mb-6">Extra-Curricular Activities</h3>
          <div className="flex flex-wrap justify-center gap-3">
            {activities.map((activity, index) => (
              <span 
                key={index}
                className="bg-[#1a2744] text-[#94a3b8] text-sm px-4 py-2 border border-[#d4af37]/10 hover:border-[#d4af37]/30 hover:text-white transition-all"
              >
                {activity}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#d4af37]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 border-2 border-[#d4af37] flex items-center justify-center">
                <span className="font-display text-lg font-bold text-[#d4af37]">VB</span>
              </div>
              <span className="font-display text-xl text-white font-semibold">Vaibhav Bhore</span>
            </div>

            {/* Copyright */}
            <div className="text-[#64748b] text-sm">
              © {currentYear} All rights reserved.
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-4">
              <a
                href={data.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#1a2744] border border-[#d4af37]/20 flex items-center justify-center text-[#94a3b8] hover:text-[#d4af37] hover:border-[#d4af37]/50 transition-all"
              >
                <Linkedin size={18} />
              </a>
              <a
                href={`mailto:${data.email}`}
                className="w-10 h-10 bg-[#1a2744] border border-[#d4af37]/20 flex items-center justify-center text-[#94a3b8] hover:text-[#d4af37] hover:border-[#d4af37]/50 transition-all"
              >
                <Mail size={18} />
              </a>
              <a
                href={`tel:${data.phone}`}
                className="w-10 h-10 bg-[#1a2744] border border-[#d4af37]/20 flex items-center justify-center text-[#94a3b8] hover:text-[#d4af37] hover:border-[#d4af37]/50 transition-all"
              >
                <Phone size={18} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export { Contact, Footer };
